// The Yogurt Café rhythm minigame's playfield. A 4-lane note highway rendered
// as a DOM overlay on top of the 3D café. Notes fall toward a hit line; the
// player taps the lane (screen tap or D/F/J/K) as each note crosses it. Timing
// is read from the Groove's audio clock so notes and music stay locked.
//
// This module owns only the highway + scoring; the café scene owns the 3D world
// and shows the ready/results panels. Call mount() to build the overlay, begin()
// once the groove is playing, update() every frame, and unmount() to tear down.

const KEYS = ['d', 'f', 'j', 'k'];
const LANE_COLORS = ['#E6537B', '#F49B33', '#7FBF6A', '#3D5FC0']; // rasp/tang/sprout/cobalt
const APPROACH = 1.9;     // seconds a note is visible before it reaches the line
const PERFECT = 0.05;     // ± timing window (s)
const GOOD    = 0.12;

export class RhythmGame {
  constructor(song, groove, { onFinish } = {}){
    this.song = song;
    this.groove = groove;
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    // fresh, judgeable copy of the beatmap
    this.notes = song.notes.map(n => ({ time: n.time, lane: n.lane, el: null, spawned: false, judged: false }));

    this.counts = { perfect: 0, good: 0, miss: 0 };
    this.combo = 0; this.maxCombo = 0; this.score = 0;

    this._onKey = this._onKey.bind(this);
    this._onResize = () => this._measure();
  }

  mount(root){
    const el = document.createElement('div');
    el.className = 'rg';
    el.innerHTML = `
      <div class="rg-field">
        ${[0,1,2,3].map(i => `
          <div class="rg-lane" data-lane="${i}" style="--lane:${LANE_COLORS[i]}">
            <span class="rg-key">${KEYS[i].toUpperCase()}</span>
          </div>`).join('')}
        <div class="rg-hit"></div>
      </div>
      <div class="rg-combo" hidden></div>
      <div class="rg-judge-wrap"></div>`;
    (root || document.body).appendChild(el);
    this.el = el;
    this.field = el.querySelector('.rg-field');
    this.comboEl = el.querySelector('.rg-combo');
    this.judgeWrap = el.querySelector('.rg-judge-wrap');

    // lane taps (touch + mouse). pointerdown = lowest latency.
    el.querySelectorAll('.rg-lane').forEach(laneEl => {
      laneEl.addEventListener('pointerdown', e => {
        e.preventDefault();
        this._hit(Number(laneEl.dataset.lane), laneEl);
      });
    });
    window.addEventListener('keydown', this._onKey);
    window.addEventListener('resize', this._onResize);
    this._measure();
  }

  begin(){ this.active = true; }

  _measure(){
    if (!this.field) return;
    const r = this.field.getBoundingClientRect();
    this.fieldH = r.height;
    this.hitY = r.height - 96;      // hit line sits 96px up from the lane base
  }

  _onKey(e){
    if (!this.active || e.repeat) return;
    const lane = KEYS.indexOf(e.key.toLowerCase());
    if (lane === -1) return;
    const laneEl = this.field.querySelector(`.rg-lane[data-lane="${lane}"]`);
    this._hit(lane, laneEl);
  }

  _hit(lane, laneEl){
    if (!this.active) return;
    if (laneEl){ laneEl.classList.remove('lit'); void laneEl.offsetWidth; laneEl.classList.add('lit'); }
    const now = this.groove.now();

    // nearest un-judged, on-screen note in this lane within the good window
    let best = null, bestDt = GOOD + 1;
    for (const n of this.notes){
      if (n.lane !== lane || n.judged || !n.spawned) continue;
      const dt = Math.abs(n.time - now);
      if (dt < bestDt){ bestDt = dt; best = n; }
    }
    if (!best || bestDt > GOOD) return;   // empty tap — harmless, no combo break

    const result = bestDt <= PERFECT ? 'perfect' : 'good';
    best.judged = true;
    this.counts[result]++;
    this.combo++;
    this.maxCombo = Math.max(this.maxCombo, this.combo);
    this.score += (result === 'perfect' ? 100 : 60) + Math.min(this.combo, 25) * 2;
    this.groove.blip(true);
    this._popNote(best, result);
    this._judge(result === 'perfect' ? 'Perfect!' : 'Good', result);
    this._showCombo();
  }

  update(){
    if (!this.active || this.finished) return;
    const now = this.groove.now();

    for (const n of this.notes){
      if (n.judged && !n.el) continue;

      if (!n.spawned && now >= n.time - APPROACH){
        this._spawnNote(n);
      }
      if (n.spawned && !n.judged){
        const ttl = n.time - now;
        this._place(n, ttl);
        if (now - n.time > GOOD){       // slipped past the window
          n.judged = true;
          this.counts.miss++;
          this.combo = 0;
          this._missNote(n);
          this._judge('Miss', 'miss');
          this._showCombo();
        }
      }
    }

    if (now >= this.song.lastNote + 0.5){ this._finish(); }
  }

  _spawnNote(n){
    const el = document.createElement('div');
    el.className = 'rg-note';
    el.style.setProperty('--lane', LANE_COLORS[n.lane]);
    el.style.left = `${n.lane * 25}%`;
    this.field.appendChild(el);
    n.el = el; n.spawned = true;
  }

  _place(n, ttl){
    // y=0 at spawn (top), y=hitY when ttl=0; keeps falling for ttl<0
    const y = this.hitY * (1 - ttl / APPROACH);
    n.el.style.transform = `translateY(${y.toFixed(1)}px)`;
  }

  _popNote(n, result){
    if (!n.el) return;
    n.el.classList.add(result === 'perfect' ? 'pop-perfect' : 'pop-good');
    const el = n.el; n.el = null;
    setTimeout(() => el.remove(), 180);
  }

  _missNote(n){
    if (!n.el) return;
    n.el.classList.add('missed');
    const el = n.el; n.el = null;
    setTimeout(() => el.remove(), 260);
  }

  _judge(text, cls){
    const j = document.createElement('div');
    j.className = `rg-judge ${cls}`;
    j.textContent = text;
    this.judgeWrap.appendChild(j);
    setTimeout(() => j.remove(), 520);
  }

  _showCombo(){
    if (this.combo >= 3){
      this.comboEl.hidden = false;
      this.comboEl.textContent = `${this.combo} combo`;
      this.comboEl.classList.remove('pulse'); void this.comboEl.offsetWidth;
      this.comboEl.classList.add('pulse');
    } else {
      this.comboEl.hidden = true;
    }
  }

  _finish(){
    if (this.finished) return;
    this.finished = true;
    this.active = false;
    const { perfect, good, miss } = this.counts;
    const hit = perfect + good;
    const total = hit + miss;
    const crumbs = perfect * 2 + good;         // payout
    const accuracy = total ? Math.round((perfect + good * 0.5) / total * 100) : 0;
    this.onFinish({ perfect, good, miss, total, maxCombo: this.maxCombo, score: this.score, accuracy, crumbs });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onResize);
    if (this.el) this.el.remove();
    this.el = null;
  }
}
